import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Upload } from 'lucide-react';

// Default credit roles
export const DEFAULT_CREDIT_ROLES = [
  'Agency', 'Creative', 'Script', 'Producer', 'Associate Producer', 'Field Producer', 
  'Fixer', 'Director', 'A.D.', 'Director of Photography', '1st A.C.', '2nd A.C.',
  'Sound', 'Production Design', 'Art Assistant', 'Hair & Makeup', 'Gaffer', 'Grip',
  'Electrician', 'Best Boy', 'Driver', 'DIT'
];

export function ProjectDefinitionSection({ project, setProject }) {
  return (
    <div className="space-y-4">
      <div>
        <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">PROJECT DEFINITION</Label>
        <Textarea
          value={project.project_definition}
          onChange={(e) => setProject({ ...project, project_definition: e.target.value })}
          rows={4}
          placeholder="What type of project is this? Context, format, what or who are we telling..."
          className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
        />
      </div>

      <div>
        <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">CLIENT / FUNDING</Label>
        <Textarea
          value={project.client_funding}
          onChange={(e) => setProject({ ...project, client_funding: e.target.value })}
          rows={2}
          placeholder="Client, commissioning entity, funding organization..."
          className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
        />
      </div>

      <div>
        <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">AGENCY (Optional)</Label>
        <Input
          value={project.agency}
          onChange={(e) => setProject({ ...project, agency: e.target.value })}
          placeholder="Responsible agency, when applicable"
          className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
        />
      </div>
    </div>
  );
}

export function ChallengeAndRoleSection({ project, setProject }) {
  return (
    <div className="space-y-4">
      <div>
        <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">THE CHALLENGE</Label>
        <Textarea
          value={project.challenge}
          onChange={(e) => setProject({ ...project, challenge: e.target.value })}
          rows={3}
          placeholder="What creative, narrative, logistical, or technical problem needed to be solved?"
          className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
        />
      </div>

      <div>
        <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">OUR ROLE</Label>
        <Textarea
          value={project.our_role}
          onChange={(e) => setProject({ ...project, our_role: e.target.value })}
          rows={3}
          placeholder="What responsibility did Baklight assume? The true commercial value..."
          className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
        />
      </div>

      <div>
        <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">THE APPROACH</Label>
        <Textarea
          value={project.approach}
          onChange={(e) => setProject({ ...project, approach: e.target.value })}
          rows={4}
          placeholder="How we thought about it and what important decisions we made. The most differentiating section..."
          className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
        />
      </div>

      <div>
        <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-2">THE RESULT</Label>
        <Textarea
          value={project.result}
          onChange={(e) => setProject({ ...project, result: e.target.value })}
          rows={3}
          placeholder="What we delivered, where it was exhibited/used, reach, awards, results or any verifiable outcome..."
          className="bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
        />
      </div>
    </div>
  );
}

export function RecognitionSection({ project, setProject, uploadingImage, handleImageUpload }) {
  const addRecognition = () => {
    setProject({
      ...project,
      recognition: [...(project.recognition || []), { award_name: '', category: '', year: '', status: 'winner', image: '' }]
    });
  };

  const removeRecognition = (index) => {
    setProject({
      ...project,
      recognition: (project.recognition || []).filter((_, i) => i !== index)
    });
  };

  const updateRecognition = (index, field, value) => {
    const updated = [...(project.recognition || [])];
    updated[index] = { ...updated[index], [field]: value };
    setProject({ ...project, recognition: updated });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">RECOGNITION (Optional)</Label>
        <Button onClick={addRecognition} size="sm" variant="outline" className="border-[hsl(var(--bk-line))]">
          <Plus size={14} className="mr-1" /> Add Award
        </Button>
      </div>

      {project.recognition && project.recognition.length > 0 ? (
        project.recognition.map((award, index) => (
          <div key={index} className="p-4 bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] rounded space-y-3">
            <div className="flex justify-between items-start">
              <Label className="text-xs">Award #{index + 1}</Label>
              <Button variant="ghost" size="sm" onClick={() => removeRecognition(index)}>
                <Trash2 size={14} className="text-red-500" />
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <Input
                value={award.award_name}
                onChange={(e) => updateRecognition(index, 'award_name', e.target.value)}
                placeholder="Award Name"
                className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
              />
              <Input
                value={award.category}
                onChange={(e) => updateRecognition(index, 'category', e.target.value)}
                placeholder="Category"
                className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Input
                value={award.year}
                onChange={(e) => updateRecognition(index, 'year', e.target.value)}
                placeholder="Year"
                className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
              />
              <Select value={award.status} onValueChange={(v) => updateRecognition(index, 'status', v)}>
                <SelectTrigger className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[110]">
                  <SelectItem value="winner">Winner</SelectItem>
                  <SelectItem value="nominee">Nominee</SelectItem>
                  <SelectItem value="finalist">Finalist</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-[hsl(var(--bk-fg-muted))] mb-1">Laurel / Award Graphic (Min: 200×200px)</Label>
              <label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (file && handleImageUpload) {
                      console.log('Uploading award image for index:', index);
                      const url = await handleImageUpload(file, `recognition_${index}`);
                      console.log('Upload returned URL:', url);
                      if (url) {
                        console.log('Updating recognition image at index', index, 'with URL:', url);
                        updateRecognition(index, 'image', url);
                      }
                    }
                  }}
                  className="hidden"
                  id={`recognition-upload-${index}`}
                  disabled={uploadingImage === `recognition_${index}`}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
                  disabled={uploadingImage === `recognition_${index}`}
                  onClick={() => document.getElementById(`recognition-upload-${index}`).click()}
                >
                  <Upload size={14} className="mr-1" />
                  {uploadingImage === `recognition_${index}` ? 'Uploading...' : 'Choose Laurel'}
                </Button>
              </label>
              {award.image && (
                <div className="mt-2">
                  <p className="text-xs text-[hsl(var(--bk-fg-muted))] mb-1">Preview:</p>
                  <img 
                    src={award.image} 
                    alt="Award" 
                    className="h-16 object-contain bg-white/5 p-2 rounded"
                    onError={(e) => {
                      console.error('Failed to load image:', award.image);
                      e.target.style.border = '2px solid red';
                    }}
                  />
                  <p className="text-xs text-[hsl(var(--bk-fg-subtle))] mt-1 break-all">{award.image}</p>
                </div>
              )}
            </div>
          </div>
        ))
      ) : (
        <p className="text-sm text-[hsl(var(--bk-fg-muted))] text-center py-4">No awards added yet</p>
      )}
    </div>
  );
}

export function CapabilitiesSection({ project, setProject, capabilities }) {
  const toggleCapability = (capId) => {
    const current = project.capabilities || [];
    if (current.includes(capId)) {
      setProject({ ...project, capabilities: current.filter(id => id !== capId) });
    } else {
      setProject({ ...project, capabilities: [...current, capId] });
    }
  };

  return (
    <div className="space-y-4">
      <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">CAPABILITIES IMPLEMENTED</Label>
      <div className="grid grid-cols-3 gap-2">
        {capabilities.map(cap => (
          <label key={cap.id} className="flex items-center gap-2 p-3 bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] rounded cursor-pointer hover:border-[hsl(var(--bk-accent))] transition-colors">
            <input
              type="checkbox"
              checked={(project.capabilities || []).includes(cap.id)}
              onChange={() => toggleCapability(cap.id)}
              className="form-checkbox"
            />
            <span className="font-body text-sm text-[hsl(var(--bk-fg))]">{cap.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export function CreditsSection({ project, setProject }) {
  const addCredit = (role = '', name = '') => {
    setProject({
      ...project,
      credits: [...(project.credits || []), { role, name }]
    });
  };

  const removeCredit = (index) => {
    setProject({
      ...project,
      credits: (project.credits || []).filter((_, i) => i !== index)
    });
  };

  const updateCredit = (index, field, value) => {
    const updated = [...(project.credits || [])];
    updated[index][field] = value;
    setProject({ ...project, credits: updated });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">CREDITS</Label>
        <div className="flex gap-2">
          <Select onValueChange={(role) => addCredit(role, '')}>
            <SelectTrigger className="w-48 bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
              <SelectValue placeholder="Quick add role..." />
            </SelectTrigger>
            <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[110] max-h-64 overflow-y-auto">
              {DEFAULT_CREDIT_ROLES.map(role => (
                <SelectItem key={role} value={role}>{role}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => addCredit('', '')} size="sm" variant="outline" className="border-[hsl(var(--bk-line))]">
            <Plus size={14} className="mr-1" /> Custom
          </Button>
        </div>
      </div>

      {project.credits && project.credits.length > 0 ? (
        <div className="space-y-2">
          {project.credits.map((credit, index) => (
            <div key={index} className="flex gap-2">
              <Input
                value={credit.role}
                onChange={(e) => updateCredit(index, 'role', e.target.value)}
                placeholder="Role"
                className="w-1/3 bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
              />
              <Input
                value={credit.name}
                onChange={(e) => updateCredit(index, 'name', e.target.value)}
                placeholder="Name"
                className="flex-1 bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
              />
              <Button variant="ghost" size="sm" onClick={() => removeCredit(index)}>
                <Trash2 size={14} className="text-red-500" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-[hsl(var(--bk-fg-muted))] text-center py-4">No credits added yet</p>
      )}
    </div>
  );
}

export function SimilarProjectsSection({ project, setProject, allProjects }) {
  const toggleSimilarProject = (projId) => {
    const current = project.similar_project_ids || [];
    if (current.includes(projId)) {
      setProject({ ...project, similar_project_ids: current.filter(id => id !== projId) });
    } else {
      if (current.length < 4) {
        setProject({ ...project, similar_project_ids: [...current, projId] });
      }
    }
  };

  // Auto-suggest: same category, published, not current project
  const suggestions = allProjects
    .filter(p => 
      p.id !== project.id && 
      p.status === 'published' && 
      p.category_id === project.category_id
    )
    .slice(0, 6);

  return (
    <div className="space-y-4">
      <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">SIMILAR PROJECTS (Max 4)</Label>
      <p className="text-xs text-[hsl(var(--bk-fg-muted))]">Auto-suggested from same category. Select up to 4.</p>
      
      <div className="grid grid-cols-2 gap-2">
        {suggestions.map(proj => (
          <label key={proj.id} className="flex items-start gap-2 p-3 bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))] rounded cursor-pointer hover:border-[hsl(var(--bk-accent))] transition-colors">
            <input
              type="checkbox"
              checked={(project.similar_project_ids || []).includes(proj.id)}
              onChange={() => toggleSimilarProject(proj.id)}
              disabled={(project.similar_project_ids || []).length >= 4 && !(project.similar_project_ids || []).includes(proj.id)}
              className="form-checkbox mt-1"
            />
            <div>
              <p className="font-body text-sm text-[hsl(var(--bk-fg))]">{proj.title}</p>
              <p className="font-mono-meta text-xs text-[hsl(var(--bk-fg-muted))]">{proj.year}</p>
            </div>
          </label>
        ))}
      </div>

      {suggestions.length === 0 && (
        <p className="text-sm text-[hsl(var(--bk-fg-muted))] text-center py-4">No similar projects available</p>
      )}
    </div>
  );
}

// Behind the Scenes Gallery Section
export function BehindTheScenesSection({ project, setProject, uploadingImage, handleImageUpload }) {
  const [newItemUrl, setNewItemUrl] = useState('');
  
  const addGalleryItem = () => {
    if (newItemUrl.trim()) {
      setProject({
        ...project,
        behind_the_scenes_gallery: [...(project.behind_the_scenes_gallery || []), newItemUrl.trim()]
      });
      setNewItemUrl('');
    }
  };
  
  const removeGalleryItem = (index) => {
    const updated = [...project.behind_the_scenes_gallery];
    updated.splice(index, 1);
    setProject({ ...project, behind_the_scenes_gallery: updated });
  };
  
  const handleFileUpload = async (file) => {
    if (!file) return;
    
    try {
      await handleImageUpload(file, 'behind_the_scenes');
      // The handleImageUpload will update the state through the parent component
    } catch (error) {
      console.error('Failed to upload:', error);
    }
  };
  
  return (
    <div className="space-y-4">
      <div>
        <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-1">BEHIND THE SCENES GALLERY</Label>
        <p className="text-xs text-[hsl(var(--bk-fg-muted))] mb-3">
          Add photos and videos from the production process
        </p>
      </div>
      
      {/* Upload or Add URL */}
      <div className="space-y-2">
        <div className="flex gap-2">
          <label className="flex-shrink-0">
            <input
              type="file"
              accept="image/*,video/*"
              onChange={(e) => handleFileUpload(e.target.files[0])}
              disabled={uploadingImage === 'behind_the_scenes'}
              className="hidden"
              id="bts-upload"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
              disabled={uploadingImage === 'behind_the_scenes'}
              onClick={() => document.getElementById('bts-upload').click()}
            >
              <Upload size={14} className="mr-2" />
              {uploadingImage === 'behind_the_scenes' ? 'Uploading...' : 'Upload'}
            </Button>
          </label>
          
          <Input
            value={newItemUrl}
            onChange={(e) => setNewItemUrl(e.target.value)}
            placeholder="Or paste image/video URL (Vimeo, YouTube, image URL)"
            className="flex-1 bg-[hsl(var(--bk-bg))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] text-sm"
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addGalleryItem())}
          />
          
          <Button 
            type="button"
            variant="outline" 
            size="sm"
            onClick={addGalleryItem}
            disabled={!newItemUrl.trim()}
            className="border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]"
          >
            <Plus size={14} className="mr-1" />
            Add
          </Button>
        </div>
      </div>
      
      {/* Gallery Items List */}
      {project.behind_the_scenes_gallery && project.behind_the_scenes_gallery.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-4">
          {project.behind_the_scenes_gallery.map((item, index) => (
            <div key={index} className="relative group">
              <div className="aspect-video rounded overflow-hidden bg-[hsl(var(--bk-bg))] border border-[hsl(var(--bk-line))]">
                {item.includes('youtube.com') || item.includes('youtu.be') || item.includes('vimeo.com') ? (
                  <div className="w-full h-full flex items-center justify-center text-xs text-[hsl(var(--bk-fg-muted))]">
                    Video: {item.substring(0, 30)}...
                  </div>
                ) : (
                  <img src={item} alt={`BTS ${index + 1}`} className="w-full h-full object-cover" />
                )}
              </div>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => removeGalleryItem(index)}
                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 h-auto"
              >
                <Trash2 size={12} />
              </Button>
            </div>
          ))}
        </div>
      )}
      
      {(!project.behind_the_scenes_gallery || project.behind_the_scenes_gallery.length === 0) && (
        <p className="text-sm text-[hsl(var(--bk-fg-muted))] text-center py-4 border border-dashed border-[hsl(var(--bk-line))] rounded">
          No behind the scenes content yet
        </p>
      )}
    </div>
  );
}
